// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Copy, RotateCcw, ThumbsDown, ThumbsUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AnswerError } from "@/features/assistant/blocks/answer-error";
import { AnswerLink } from "@/features/assistant/blocks/answer-link";
import { AnswerNote } from "@/features/assistant/blocks/answer-note";
import { AnswerTable } from "@/features/assistant/blocks/answer-table";
import { AnswerText } from "@/features/assistant/blocks/answer-text";
import { StepList } from "@/features/assistant/blocks/step-list";
import { FeedbackDetails } from "@/features/assistant/feedback-details";
import type { AssistantMessage } from "@/features/assistant/types";
import { cn } from "@/lib/utils";

export function AssistantMessageView({
  message,
  isLast,
  busy,
  onRetry,
  onFeedback,
  onSendComment,
  onRetryFeedback,
  onNavigate,
}: {
  message: AssistantMessage;
  isLast: boolean;
  busy: boolean;
  onRetry: () => void;
  onFeedback: (value: "up" | "down") => void;
  onSendComment: (reasons: string[], comment: string) => Promise<void>;
  onRetryFeedback: () => void;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const live = message.status === "working" || message.status === "streaming";
  const settled = message.status === "done" || message.status === "stopped";

  async function copy() {
    try {
      await navigator.clipboard.writeText(message.text);
      toast.success(t("assistant.actions.copied"));
    } catch {
      // Clipboard blocked: nothing to report beyond the missing toast.
    }
  }

  return (
    <div className="mb-4">
      <StepList steps={message.steps} live={message.status === "working" || message.status === "streaming"} />
      <AnswerText text={message.text} streaming={message.status === "streaming"} />
      {/* Blocks only ever append within an answer, so the index is a stable key. */}
      {message.tables.map((table, i) => (
        <AnswerTable key={i} table={table} />
      ))}
      {message.notes.map((note, i) => (
        <AnswerNote key={i} text={note} />
      ))}
      {message.links.map((link, i) => (
        <AnswerLink key={i} link={link} onNavigate={onNavigate} />
      ))}
      {message.status === "error" && (
        <AnswerError message={message.error ?? ""} onRetry={onRetry} canRetry={isLast && !busy} />
      )}
      {message.status === "stopped" && <p className="mb-1 text-muted-foreground text-xs">{t("assistant.stopped")}</p>}
      {settled && !live && (
        <div className="flex gap-0.5 text-muted-foreground">
          {message.text && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label={t("assistant.actions.copy")}
              onClick={copy}
            >
              <Copy className="size-3.5" />
            </Button>
          )}
          {isLast && !busy && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              aria-label={t("assistant.actions.regenerate")}
              onClick={onRetry}
            >
              <RotateCcw className="size-3.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className={cn("size-7", message.feedback === "up" && "text-primary")}
            aria-label={t("assistant.actions.helpful")}
            aria-pressed={message.feedback === "up"}
            onClick={() => onFeedback("up")}
          >
            <ThumbsUp className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={cn("size-7", message.feedback === "down" && "text-destructive")}
            aria-label={t("assistant.actions.notHelpful")}
            aria-pressed={message.feedback === "down"}
            onClick={() => onFeedback("down")}
          >
            <ThumbsDown className="size-3.5" />
          </Button>
        </div>
      )}
      {settled && !live && (
        <FeedbackDetails
          feedback={message.feedback}
          feedbackState={message.feedbackState}
          onSendComment={onSendComment}
          onRetryFeedback={onRetryFeedback}
        />
      )}
    </div>
  );
}
